// TODO [Matt] Since elementMap is a stateful value, this should be a proper class.

/**
 * @internal
 * A map for tracking generated event handlers on elements.
 */
const elementMap = new Map();

/**
 * @internal
 * Creates a delegated event handler and adds it to the registration map for future removal.
 * @param element The target of the delegated event.
 * @param event The event to listen for.
 * @param handler The callback to be fired.
 */
function pushHandler<T extends Event>(element: EventTarget, event: string, handler: (event: T) => void) {
    const delegate = (event: T) => {
        if((element as Element).contains(event.target as Node)) {
            handler(event);
        }
    };
    const eventMap = elementMap.get(element) || new Map();
    const handlerMap = eventMap.get(event) || new Map();
    handlerMap.set(handler, delegate);
    eventMap.set(event, handlerMap);
    elementMap.set(element, eventMap);
    return delegate;
}

/**
 * @internal
 * Locates and removes a delegated event handler from the registration map.
 * @param element The target of the delegated event.
 * @param event The event to listen for.
 * @param handler The callback to be fired.
 */
function popHandler<T extends Event>(element: EventTarget, event: string, handler: (event: T) => void) {
    const eventMap = elementMap.get(element);
    if(!eventMap) {
        return null;
    }
    const handlerMap = eventMap.get(event);
    if(!handlerMap) {
        return null;
    }
    const delegate = handlerMap.get(handler);
    if(!delegate) {
        return null;
    }
    handlerMap.delete(handler);
    return delegate as (event: T) => void;
}

/**
 * Delegates an event handler to the document root.
 * @param element The target of the delegated event.
 * @param event The event to listen for.
 * @param handler The callback to be fired.
 */
export function addDelegatedEventListener<T extends Event>(element: EventTarget, event: string, handler: (event: T) => void) {
    const delegatedHandler = pushHandler(element, event, handler);
    document.getRootNode().addEventListener(event, delegatedHandler as EventListener);
}

/**
 * Removes a delegated event handler from the document root.
 * @param element The target of the delegated event.
 * @param event The event to listen for.
 * @param handler The callback to be fired.
 */
export function removeDelegatedEventListener<T extends Event>(element: EventTarget, event: string, handler: (event: T) => void) {
    const delegatedHandler = popHandler(element, event, handler);
    if(delegatedHandler) {
        document.getRootNode().removeEventListener(event, delegatedHandler as EventListener);
    }
}

